import { computed, ref, watch } from 'vue';

import {
  type TransformedProduct,
  useStorefrontCollectionProducts
} from '@/shared/composables/useStorefrontCollectionProducts';
import { GIFT_COLLECTION_FETCH_WINDOW } from '@/shared/constants/constants';

export type GiftProduct = TransformedProduct;

// Fisher-Yates shuffle algorithm for randomizing array
const shuffleArray = <T>(array: T[]): T[] => {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
};

// Cache shuffled product IDs in sessionStorage to persist across remounts
const SHUFFLE_CACHE_KEY = 'hobbii_gift-products:shuffle-order';

const getShuffleCache = (collectionHandle: string): string[] | null => {
  try {
    const cache = sessionStorage.getItem(SHUFFLE_CACHE_KEY);
    if (!cache) return null;

    const parsed = JSON.parse(cache);
    return parsed[collectionHandle] || null;
  } catch {
    return null;
  }
};

const setShuffleCache = (collectionHandle: string, productIds: string[]) => {
  try {
    const cache = sessionStorage.getItem(SHUFFLE_CACHE_KEY);
    const parsed = cache ? JSON.parse(cache) : {};
    parsed[collectionHandle] = productIds;
    sessionStorage.setItem(SHUFFLE_CACHE_KEY, JSON.stringify(parsed));
  } catch {
    // Silently fail if sessionStorage is not available
  }
};

export const useGiftProducts = (collectionHandle: string, maxProducts = 3) => {
  const {
    products: transformedProducts,
    loading,
    error,
    refetch
  } = useStorefrontCollectionProducts(collectionHandle, {
    // Fetch more than needed for randomization; shared with useGiftAvailability
    // so both surfaces query the identical product set (one Apollo request).
    first: GIFT_COLLECTION_FETCH_WINDOW
  });

  const shuffledProducts = ref<TransformedProduct[]>([]);

  // Shuffle products once when loaded, using cached order if available
  watch(
    transformedProducts,
    (newProducts: TransformedProduct[]) => {
      if (newProducts.length === 0) return;

      // Try to get cached shuffle order
      const cachedOrder = getShuffleCache(collectionHandle);

      if (cachedOrder) {
        // Restore order from cache
        const orderedProducts: TransformedProduct[] = [];
        const productsMap = new Map(newProducts.map((p) => [p.id, p]));

        for (const id of cachedOrder) {
          const product = productsMap.get(id);
          if (product) {
            orderedProducts.push(product);
            if (orderedProducts.length >= maxProducts) break;
          }
        }

        // If cache didn't have enough products, append remaining ones
        if (orderedProducts.length < maxProducts) {
          const usedIds = new Set(orderedProducts.map((p) => p.id));
          const remaining = newProducts.filter((p) => !usedIds.has(p.id));
          orderedProducts.push(
            ...remaining.slice(0, maxProducts - orderedProducts.length)
          );
        }

        shuffledProducts.value = orderedProducts;
      } else {
        // No cache - shuffle and store the order
        const shuffled = shuffleArray(newProducts);
        const selected = shuffled.slice(0, maxProducts);
        shuffledProducts.value = selected;

        // Cache the shuffled order (product IDs only)
        setShuffleCache(
          collectionHandle,
          shuffled.map((p) => p.id)
        );
      }
    },
    { immediate: true }
  );

  const products = computed(() => shuffledProducts.value);

  return {
    products, // Displayed subset (3 random products)
    allProducts: transformedProducts, // All fetched products for validation
    loading,
    error,
    refetch
  };
};
